import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Search,
  Bot,
  Check,
  TrendingUp,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../i18n';
import {
  ToastMark,
  SquareMark,
  CloverMark,
  GoogleMark,
  ChatGPTMark,
  GeminiMark,
  PerplexityMark,
} from './BrandIcons';

/* =================================================================
   GrowthEngines — indexly.ai-style asymmetric bento.
   Each pillar card leads with a CSS-built product visual (60%+ of
   the card), followed by minimal copy. Canonical pillar order in
   i18n services[]: 0 Foundation, 1 Index AI (featured), 2 AI Agents,
   3 Revenue Growth, 4 Data & Analytics.
   ================================================================= */

const serviceHrefs = [
  '/it-pos-systems',
  '/index-ai',
  '/ai-agents-for-restaurants',
  '/restaurant-revenue-growth',
  '/services',
];

const badgeTone = [
  'bg-white/5 text-neutral-300 border-white/15',
  'bg-[#F77F11] text-white border-[#F77F11]',
  'bg-orange-500/15 text-[#ffb077] border-orange-400/30',
  'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  'bg-violet-500/15 text-violet-300 border-violet-400/30',
];

const Panel = ({ children, className = '' }) => (
  <div className={`relative rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden ${className}`}>
    {children}
  </div>
);

/* ── Mini product visuals (pure CSS + motion, no stock imagery) ── */

function VisualIndexAi({ lang }) {
  return (
    <Panel className="p-4 h-full flex flex-col justify-center gap-3">
      <div className="flex items-center gap-2.5 rounded-full bg-white/[0.06] border border-white/10 px-3.5 h-9">
        <Search className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
        <span className="text-[12px] text-neutral-300 truncate">
          {lang === 'es' ? '"mejor pasta cerca de mí"' : '"best pasta near me"'}
        </span>
        <span className="ml-auto inline-block w-[2px] h-3.5 bg-[#F77F11] animate-pulse" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.35 }}
        className="rounded-lg border border-[#F77F11]/40 bg-[#F77F11]/10 px-3.5 py-2.5"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#F77F11] shrink-0" />
          <p className="text-[12px] text-white font-semibold truncate">
            {lang === 'es' ? 'Tu restaurante — citado por la IA' : 'Your restaurant — cited by AI'}
          </p>
        </div>
        <p className="mt-1 text-[12.5px] text-neutral-500 leading-snug line-clamp-2">
          {lang === 'es'
            ? '"…la mejor opción es este local: pasta fresca, 4.9★ y reservas directas."'
            : '"…the top pick is this spot: fresh pasta, 4.9★ and direct reservations."'}
        </p>
      </motion.div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] tracking-[0.16em] uppercase font-bold text-neutral-500">
          {lang === 'es' ? 'Visible en' : 'Visible on'}
        </span>
        <ChatGPTMark /> <GeminiMark /> <PerplexityMark /> <GoogleMark />
      </div>
    </Panel>
  );
}

function VisualAgents({ lang }) {
  return (
    <Panel className="p-4 h-full flex flex-col justify-center gap-2.5">
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="self-start max-w-[85%] rounded-2xl rounded-bl-sm bg-white/[0.08] px-3.5 py-2 text-[12px] text-neutral-200"
      >
        {lang === 'es' ? '¿Mesa para 4 esta noche a las 8?' : 'Table for 4 tonight at 8?'}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.55 }}
        className="self-end max-w-[85%] rounded-2xl rounded-br-sm bg-[#F77F11] px-3.5 py-2 text-[12px] text-white"
      >
        {lang === 'es' ? '¡Confirmado! Os esperamos a las 20:00 ✓' : "Confirmed! See you at 8:00 PM ✓"}
      </motion.div>
      <p className="mt-1 text-[10.5px] text-neutral-500 flex items-center gap-1.5">
        <Bot className="w-3 h-3 text-[#F77F11]" />
        {lang === 'es' ? 'Respondido por tu agente AI en 3 segundos' : 'Answered by your AI agent in 3 seconds'}
      </p>
    </Panel>
  );
}

function VisualFoundation({ lang }) {
  return (
    <Panel className="p-4 h-full flex flex-col items-center justify-center gap-3">
      <div className="flex items-center gap-2">
        <ToastMark className="ring-2 ring-[#0F1117]" />
        <SquareMark className="ring-2 ring-[#0F1117]" />
        <CloverMark className="ring-2 ring-[#0F1117]" />
        <GoogleMark className="ring-2 ring-[#0F1117]" />
      </div>
      <div className="relative w-full h-6 flex items-center justify-center" aria-hidden="true">
        <span className="absolute inset-x-8 top-1/2 h-px bg-gradient-to-r from-transparent via-[#F77F11]/60 to-transparent" />
        <motion.span
          animate={{ scale: [1, 1.25, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-3 h-3 rounded-full bg-[#F77F11] shadow-[0_0_18px_rgba(247,127,17,0.9)]"
        />
      </div>
      <p className="text-[10.5px] text-neutral-500 text-center">
        {lang === 'es' ? '40+ plataformas · un solo sistema' : '40+ platforms · one system'}
      </p>
    </Panel>
  );
}

function VisualGrowth({ lang }) {
  const bars = [34, 48, 42, 62, 74, 92];
  return (
    <Panel className="p-4 h-full flex flex-col justify-center">
      <div className="flex items-end gap-2 h-20">
        {bars.map((h, i) => (
          <motion.span
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease: 'easeOut' }}
            className={`flex-1 rounded-t-md ${i === bars.length - 1 ? 'bg-gradient-to-t from-[#F77F11] to-[#ffb077]' : 'bg-white/15'}`}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10.5px] text-neutral-500">{lang === 'es' ? 'Ingresos directos' : 'Direct revenue'}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[11px] font-bold px-2 py-0.5">
          <TrendingUp className="w-3 h-3" /> +18%
        </span>
      </div>
    </Panel>
  );
}

function VisualAnalytics({ lang }) {
  const kpis =
    lang === 'es'
      ? [
          { l: 'Pedidos', v: '+24%' },
          { l: 'Reseñas', v: '4.8★' },
          { l: 'Ticket', v: '+9%' },
          { l: 'Retención', v: '+12%' },
        ]
      : [
          { l: 'Orders', v: '+24%' },
          { l: 'Reviews', v: '4.8★' },
          { l: 'Ticket', v: '+9%' },
          { l: 'Retention', v: '+12%' },
        ];
  return (
    <Panel className="p-4 h-full grid grid-cols-2 gap-2 content-center">
      {kpis.map((k, i) => (
        <motion.div
          key={k.l}
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: 0.1 + i * 0.08 }}
          className="rounded-lg bg-white/[0.06] border border-white/10 px-3 py-2"
        >
          <p className="text-[9.5px] tracking-[0.12em] uppercase font-bold text-neutral-500">{k.l}</p>
          <p className="text-[15px] font-bold text-white tabular-nums">{k.v}</p>
        </motion.div>
      ))}
    </Panel>
  );
}

/* ── Bento card shell ── */

function PillarCard({ s, idx, lang, visual: Visual, className = '', featured = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: (idx % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`platform-pillar-${idx}`}
      className={className}
    >
      <Link
        to={serviceHrefs[idx]}
        data-testid={`platform-pillar-link-${idx}`}
        className={`group flex flex-col h-full rounded-3xl border bg-white/[0.02] p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.04] ${
          featured
            ? 'border-[#F77F11]/40 shadow-[0_40px_110px_-40px_rgba(247,127,17,0.5)] hover:border-[#F77F11]/70'
            : 'border-white/10 hover:border-[#F77F11]/50 hover:shadow-[0_34px_90px_-40px_rgba(247,127,17,0.45)]'
        }`}
      >
        <div className={`${featured ? 'min-h-[220px]' : 'min-h-[170px]'} flex-1`}>
          <Visual lang={lang} />
        </div>
        <div className="mt-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.22em] font-bold uppercase text-neutral-500 group-hover:text-[#F77F11] transition-colors">
              {s.capability}
            </p>
            <h3 className="mt-1 text-white font-bold text-[18px] tracking-[-0.01em] group-hover:text-[#F77F11] transition-colors">
              {s.title}
            </h3>
            <p className="mt-1.5 text-[14.5px] text-neutral-500 leading-relaxed line-clamp-2">{s.description}</p>
          </div>
          <span className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-neutral-400 group-hover:bg-[#F77F11] group-hover:border-[#F77F11] group-hover:text-white transition-all duration-300">
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
        <div className="mt-4">
          <span
            className={`inline-flex items-center gap-1 text-[9.5px] font-bold tracking-[0.18em] uppercase rounded-full px-2.5 py-1 border ${badgeTone[idx]}`}
            data-testid={`platform-badge-${idx}`}
          >
            {s.badge}
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function GrowthEngines() {
  const { t, lang } = useT();
  const services = t('services');
  const visuals = [VisualFoundation, VisualIndexAi, VisualAgents, VisualGrowth, VisualAnalytics];

  return (
    <section
      className="relative bg-[#0F1117] text-white py-24 lg:py-32 overflow-hidden"
      id="automation"
      data-testid="platform-capabilities"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(247,127,17,0.18), transparent 60%)',
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="kx-eyebrow-dark mb-4" data-testid="platform-eyebrow">
            {t('engines.eyebrow')}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1]">
            {t('engines.titleA')}
            <br />
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {t('engines.titleB')}
            </span>
          </h2>
          <p className="mt-5 text-neutral-300 text-base sm:text-[17px] leading-relaxed max-w-2xl mx-auto" data-testid="platform-subtitle">
            {t('engines.subtitle')}
          </p>
        </motion.div>

        {/* Asymmetric bento — featured Index AI card + supporting pillars */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          <PillarCard s={services[1]} idx={1} lang={lang} visual={visuals[1]} featured className="md:col-span-4" />
          <PillarCard s={services[2]} idx={2} lang={lang} visual={visuals[2]} className="md:col-span-2" />
          <PillarCard s={services[0]} idx={0} lang={lang} visual={visuals[0]} className="md:col-span-2" />
          <PillarCard s={services[3]} idx={3} lang={lang} visual={visuals[3]} className="md:col-span-2" />
          <PillarCard s={services[4]} idx={4} lang={lang} visual={visuals[4]} className="md:col-span-2" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mt-16 flex flex-col items-center gap-3"
        >
          <Button
            asChild
            variant="outline"
            className="bg-transparent border-white/20 text-white hover:bg-[#F77F11] hover:border-[#F77F11] px-6 h-12 rounded-full text-[15px] font-semibold"
            data-testid="platform-cta"
          >
            <Link to="/services">
              {t('engines.cta')} <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>

          <p
            className="text-[11px] sm:text-[11.5px] tracking-[0.18em] uppercase font-medium text-neutral-500"
            data-testid="platform-microstats"
          >
            <span className="text-neutral-300">5</span>
            {' '}{t('engines.microStat.engines')}{' '}
            <span className="mx-2 text-neutral-600">·</span>
            <span className="text-neutral-300">40+</span>
            {' '}{t('engines.microStat.integrations')}{' '}
            <span className="mx-2 text-neutral-600">·</span>
            <span className="text-[#F77F11]">∞</span>
            {' '}{t('engines.microStat.intelligence')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
